// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

import SwiperCore, { A11y, Autoplay, Controller, Grid, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import ArrowNavigation, { ArrowNavigationProps } from "@steiner/App/Components/XngCarousel/ArrowNavigation";
import HorizontalScrollIndicator from "@steiner/App/Components/XngCarousel/HorizontalScrollIndicator";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { xClassNames } from "@steiner/XDL/Utilities";
import { mergeWithCss } from "@steiner/XDL/Utilities/mergeWithCss";
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { Transition } from "react-transition-group";
import { Swiper as SwiperClass } from "swiper/types";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay, Grid]);

export interface SwiperInstance extends SwiperClass {}

export type ArrowPosition = "left" | "right" | "top" | "bottom";

export type ArrowType = "next" | "prev";

export type Props = SwiperProps & {
    shadowNavigation?: boolean;
    arrowStyle?: ArrowNavigationProps["arrowStyle"];
    extendedStyles?: SwiperStyle;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

export const swiperStyle: SwiperStyle = {};

const XngCarousel: FC<Props> = props => {
    const {
        children,
        className,
        extendedStyles,
        shadowNavigation = false,
        arrowStyle = "default",
        grid,
        modules,
        slidesPerView = "auto",
        freeMode = false,
        draggable = false,
        centerInsufficientSlides = false,
        updateOnWindowResize = true,
        watchOverflow = true,
        scrollbar = false,
        pagination = false,
        spaceBetween = 20,
        simulateTouch = false,
        navigation = true,
        ...swiperProps
    } = props;
    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [showScrollLeft, setShowScrollLeft] = useState(false);
    const [showScrollRight, setShowScrollRight] = useState(false);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));
    const isSmallMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isSmallMobileScreen);
    // XNG-Changes
    const leftIndicatorRef = React.useRef<HTMLDivElement>(null);
    const rightIndicatorRef = React.useRef<HTMLDivElement>(null);

    let paginationOptions;
    let scrollbarOptions;

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            await require("swiper/swiper-bundle.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    if (!isSwiperCssImported) {
        return null;
    }

    if (scrollbar === true) {
        scrollbarOptions = { draggable: true, el: ".swiper-scrollbar", dragSize: 50 };
    }

    if (pagination === true) {
        paginationOptions = { clickable: true, el: ".swiper-pagination" };
    }

    const showPaginationContainer = !!(pagination || scrollbar);

    return (
        <StyledWrapper
            {...styles.container}
            className={xClassNames.append(["xng-carousel", className])}
            data-test-selector="xng-carousel"
        >
            <Swiper
                {...swiperProps}
                observeParents
                observer={swiperProps.loop ? undefined : true}
                grid={grid}
                modules={modules}
                pagination={paginationOptions}
                scrollbar={scrollbarOptions}
                navigation={navigation}
                slidesPerView={slidesPerView}
                spaceBetween={spaceBetween}
                simulateTouch={simulateTouch}
                watchOverflow={watchOverflow}
                freeMode={freeMode}
                draggable={draggable}
                centerInsufficientSlides={centerInsufficientSlides}
                updateOnWindowResize={updateOnWindowResize}
            >
                {shadowNavigation && (
                    <Transition
                        mountOnEnter
                        unmountOnExit
                        in={showScrollLeft}
                        timeout={{ enter: 75, exit: 250 }}
                        nodeRef={leftIndicatorRef}
                    >
                        {state => (
                            <HorizontalScrollIndicator
                                direction="left"
                                transitionState={state}
                                ref={leftIndicatorRef}
                            />
                        )}
                    </Transition>
                )}

                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props as any, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {shadowNavigation && (
                    <Transition
                        mountOnEnter
                        unmountOnExit
                        in={showScrollRight}
                        timeout={{ enter: 75, exit: 250 }}
                        nodeRef={rightIndicatorRef}
                    >
                        {state => (
                            <HorizontalScrollIndicator
                                direction="right"
                                transitionState={state}
                                ref={rightIndicatorRef}
                            />
                        )}
                    </Transition>
                )}

                {navigation && (
                    <>
                        <ArrowNavigation arrowStyle={arrowStyle} arrowType="prev" />
                        <ArrowNavigation arrowStyle={arrowStyle} arrowType="next" />
                    </>
                )}
                {showPaginationContainer && (
                    <StyledWrapper className="xng-pagination-container">
                        {pagination && <div className="swiper-pagination" />}
                        {scrollbar && <div className="swiper-scrollbar" />}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

export default XngCarousel;
